/**
 * AccountManager - 账户管理器
 * 
 * 管理用户账户和余额
 */

import type { Account, Transaction, TransactionType, TransactionStatus } from '../types/user.types';
import { logger } from '../utils/logger';
import { storage } from '../utils/storage';
import { DepositWithdrawalManager } from './DepositWithdrawalManager';

export class AccountManager {
  public accounts: Map<string, Account>;  // 改为 public，供 DepositWithdrawalManager 访问
  private depositWithdrawalManager?: DepositWithdrawalManager;

  constructor() {
    this.accounts = new Map();
    this.loadAccounts();
    logger.log('AccountManager initialized');
  }

  /**
   * 设置依赖（延迟注入）
   */
  setDependencies(
    userManager: any,
    tradingSettingsManager: any,
    brokerSettingsManager: any
  ): void {
    this.depositWithdrawalManager = new DepositWithdrawalManager(
      this,
      userManager,
      tradingSettingsManager,
      brokerSettingsManager
    );
  }

  /**
   * 保存账户到 localStorage（公开方法）
   */
  public saveAccount(account: Account): void {
    const key = `account_${account.uid}`;
    storage.set(key, account);
  }

  /**
   * 创建账户
   */
  createAccount(uid: string, initialBalance: number = 10000): Account {
    const account: Account = {
      accountId: uid,
      uid,
      balance: initialBalance,
      availableBalance: initialBalance,
      frozenMargin: 0,
      unrealizedPnl: 0,
      realizedPnl: 0,
      // 费率配置将从 broker 的全局设置动态获取，这里不再初始化
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    this.accounts.set(uid, account);
    this.saveAccount(account);
    
    logger.log(`✅ Account created for user ${uid}: ${initialBalance} USDT`);
    return account;
  }

  /**
   * 获取账户
   */
  getAccount(uid: string): Account {
    let account = this.accounts.get(uid);
    
    if (!account) {
      // 尝试从 localStorage 加载
      account = this.loadAccount(uid);
      
      if (!account) {
        throw new Error(`Account not found: ${uid}`);
      }
      
      this.accounts.set(uid, account);
    }
    
    return { ...account };  // 返回副本
  }

  /**
   * 冻结保证金
   */
  freezeMargin(uid: string, margin: number): boolean {
    let account: Account;
    try {
      account = this.getAccount(uid);

      if (account.availableBalance < margin) {
        logger.warn(`Insufficient balance for user ${uid}: ${account.availableBalance} < ${margin}`);
        return false;
      }

      account.availableBalance -= margin;
      account.frozenMargin += margin;
      account.updatedAt = Date.now();
    } catch (error) {
      logger.warn(`Account not found for user ${uid}, cannot freeze margin`);
      return false;
    }

    this.accounts.set(uid, account);
    this.saveAccount(account);
    
    logger.log(`💰 Frozen margin for user ${uid}: ${margin.toFixed(2)} USDT`);
    return true;
  }

  /**
   * 释放保证金
   */
  releaseMargin(uid: string, margin: number): void {
    const account = this.getAccount(uid);
    
    account.frozenMargin -= margin;
    account.availableBalance += margin;
    account.updatedAt = Date.now();
    
    // 确保不为负数
    account.frozenMargin = Math.max(0, account.frozenMargin);
    
    this.accounts.set(uid, account);
    this.saveAccount(account);
    
    logger.log(`💸 Released margin for user ${uid}: ${margin.toFixed(2)} USDT`);
  }

  /**
   * 更新余额（盈亏结算）
   */
  updateBalance(uid: string, pnl: number, fee: number): void {
    const account = this.getAccount(uid);
    
    const netPnl = pnl - fee;
    account.balance += netPnl;
    account.availableBalance += netPnl;
    account.realizedPnl += pnl;
    account.updatedAt = Date.now();
    
    this.accounts.set(uid, account);
    this.saveAccount(account);
    
    logger.log(`💰 Updated balance for user ${uid}: PnL=${pnl.toFixed(2)}, Fee=${fee.toFixed(2)}, New Balance=${account.balance.toFixed(2)}`);
  }

  /**
   * 更新未实现盈亏
   */
  updateUnrealizedPnl(uid: string, unrealizedPnl: number): void {
    const account = this.getAccount(uid);
    
    account.unrealizedPnl = unrealizedPnl;
    account.updatedAt = Date.now();
    
    this.accounts.set(uid, account);
    // 未实现盈亏不持久化，只在内存中
  }


  /**
   * 从 localStorage 加载账户
   */
  private loadAccount(uid: string): Account | undefined {
    const key = `account_${uid}`;
    return storage.get(key, undefined);
  }

  /**
   * 加载所有账户
   */
  private loadAccounts(): void {
    try {
      // 从 localStorage 加载所有账户
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('account_')) {
          const uid = key.replace('account_', '');
          const account = this.loadAccount(uid);
          if (account) {
            this.accounts.set(uid, account);
          }
        }
      }
      
      logger.log(`📊 Loaded ${this.accounts.size} accounts from storage`);
    } catch (error) {
      logger.error('Failed to load accounts:', error);
    }
  }

  /**
   * 获取所有账户（管理员功能）
   */
  getAllAccounts(): Account[] {
    return Array.from(this.accounts.values());
  }

  /**
   * 删除账户
   */
  deleteAccount(uid: string): void {
    this.accounts.delete(uid);
    const key = `account_${uid}`;
    localStorage.removeItem(key);
    
    logger.log(`🗑️ Account deleted: ${uid}`);
  }

  /**
   * 重置账户（用于测试）
   */
  resetAccount(uid: string, initialBalance: number = 10000): Account {
    this.deleteAccount(uid);
    return this.createAccount(uid, initialBalance);
  }

  // ==================== 出入金功能 ====================

  /**
   * 记录交易
   */
  recordTransaction(transaction: Transaction): string {
    const key = `transactions_${transaction.uid}`;
    const transactions = storage.get<Transaction[]>(key, []);
    transactions.unshift(transaction);  // 最新的在前
    
    // 限制最多保存 1000 条
    if (transactions.length > 1000) {
      transactions.splice(1000);
    }
    
    storage.set(key, transactions);
    
    return transaction.transactionId;
  }

  /**
   * 获取所有交易记录（不过滤）
   */
  getTransactions(uid: string): Transaction[] {
    const key = `transactions_${uid}`;
    return storage.get<Transaction[]>(key, []);
  }

  /**
   * 获取交易历史
   */
  getTransactionHistory(
    uid: string,
    options?: {
      type?: TransactionType;
      status?: TransactionStatus;
      startTime?: number;
      endTime?: number;
      limit?: number;
      offset?: number;
    }
  ): Transaction[] {
    const key = `transactions_${uid}`;
    let transactions = storage.get<Transaction[]>(key, []);
    
    // 筛选
    if (options?.type) {
      transactions = transactions.filter(tx => tx.type === options.type);
    }
    
    if (options?.status) {
      transactions = transactions.filter(tx => tx.status === options.status);
    }
    
    if (options?.startTime) {
      transactions = transactions.filter(tx => tx.createdAt >= options.startTime!);
    }
    
    if (options?.endTime) {
      transactions = transactions.filter(tx => tx.createdAt <= options.endTime!);
    }
    
    // 分页
    const offset = options?.offset || 0;
    const limit = options?.limit || 100;
    
    return transactions.slice(offset, offset + limit);
  }

  /**
   * 获取存取款统计
   */
  getDepositWithdrawalStats(uid: string): {
    totalDeposit: number;
    totalWithdrawal: number;
    totalDepositFee: number;
    totalBrokerRebate: number;
    totalReferrerReward: number;
    netFlow: number;
  } {
    const transactions = this.getTransactionHistory(uid, { limit: 10000 });
    
    let totalDeposit = 0;
    let totalWithdrawal = 0;
    let totalDepositFee = 0;
    let totalBrokerRebate = 0;
    let totalReferrerReward = 0;
    
    transactions.forEach(tx => {
      switch (tx.type) {
        case 'deposit':
          totalDeposit += tx.amount;
          break;
        case 'withdrawal':
          totalWithdrawal += Math.abs(tx.amount);
          break;
        case 'deposit_fee':
          totalDepositFee += Math.abs(tx.amount);
          break;
        case 'broker_rebate_in':
          totalBrokerRebate += tx.amount;
          break;
        case 'referrer_reward':
          totalReferrerReward += tx.amount;
          break;
      }
    });
    
    const netFlow = totalDeposit - totalWithdrawal;
    
    return {
      totalDeposit,
      totalWithdrawal,
      totalDepositFee,
      totalBrokerRebate,
      totalReferrerReward,
      netFlow
    };
  }

  // ==================== 出入金委托方法 ====================

  /**
   * 存款
   */
  async deposit(uid: string, amount: number, paymentMethod: string) {
    if (!this.depositWithdrawalManager) {
      throw new Error('DepositWithdrawalManager not initialized');
    }
    return this.depositWithdrawalManager.deposit(uid, amount, paymentMethod);
  }

  /**
   * 取款
   */
  async withdraw(uid: string, amount: number, walletAddress: string) {
    if (!this.depositWithdrawalManager) {
      throw new Error('DepositWithdrawalManager not initialized');
    }
    return this.depositWithdrawalManager.withdraw(uid, amount, walletAddress);
  }

  /**
   * 验证是否可以取款
   */
  canWithdraw(uid: string, amount: number) {
    if (!this.depositWithdrawalManager) {
      throw new Error('DepositWithdrawalManager not initialized');
    }
    return this.depositWithdrawalManager.canWithdraw(uid, amount);
  }

  /**
   * 获取客户存款记录（交易商视角）
   */
  getClientDepositRecords(brokerUid: string, limit?: number) {
    if (!this.depositWithdrawalManager) {
      return [];
    }
    return this.depositWithdrawalManager.getClientDepositRecords(brokerUid, limit);
  }

  /**
   * 标记客户存款记录为已读
   */
  markDepositRecordAsRead(brokerUid: string, recordId: string) {
    if (!this.depositWithdrawalManager) {
      return;
    }
    this.depositWithdrawalManager.markDepositRecordAsRead(brokerUid, recordId);
  }

  // ==================== 推荐人奖励统计方法 ====================

  /**
   * 获取推荐人奖励统计
   * @param referrerUid 推荐人UID
   * @returns 推荐人奖励统计数据
   */
  getReferrerRewardStats(referrerUid: string): {
    totalRewards: number;      // 总奖励金额
    rewardCount: number;       // 奖励次数
    rewardTransactions: Transaction[];  // 奖励交易记录
  } {
    const transactions = this.getTransactions(referrerUid);
    
    // 筛选推荐人奖励交易
    const rewardTransactions = transactions.filter(
      (tx: Transaction) => tx.type === 'referrer_reward' && tx.status === 'completed'
    );

    const totalRewards = rewardTransactions.reduce((sum: number, tx: Transaction) => sum + tx.amount, 0);

    return {
      totalRewards,
      rewardCount: rewardTransactions.length,
      rewardTransactions
    };
  }

  /**
   * 获取被推荐人的充值统计（用于计算推荐人可获得的奖励）
   * @param referredUid 被推荐人UID
   * @returns 充值统计数据
   */
  getReferredUserDepositStats(referredUid: string): {
    totalDeposits: number;     // 总充值金额
    totalFees: number;         // 总手续费
    depositCount: number;      // 充值次数
  } {
    const transactions = this.getTransactions(referredUid);
    
    // 筛选充值交易
    const depositTransactions = transactions.filter(
      (tx: Transaction) => tx.type === 'deposit' && tx.status === 'completed'
    );

    const totalDeposits = depositTransactions.reduce((sum: number, tx: Transaction) => sum + tx.amount, 0);
    const totalFees = depositTransactions.reduce((sum: number, tx: Transaction) => sum + (tx.fee || 0), 0);

    return {
      totalDeposits,
      totalFees,
      depositCount: depositTransactions.length
    };
  }
}

